/* eslint-disable prettier/prettier */
import React from 'react';
import { Box, Flex, Text, Icon, Badge, VStack, SimpleGrid } from '@chakra-ui/react';
import { Buildings, CurrencyDollarSimple, Receipt } from 'phosphor-react';
import { ICompany } from '../../../../../hooks/useCompanies';
import {
  ITaxAnalysisSNCompany,
  formatarEmReais,
  formatarPercentual,
} from '../../../../../types/taxAnalysis';
import { formatCNPJ } from '../../../../../utils/FormatFunctions';

interface TaxCardSNProps {
  company: ICompany;
  companyWithTax: ITaxAnalysisSNCompany;
  // eslint-disable-next-line no-unused-vars
  onClick: (company: ICompany) => void;
}

function TaxCardSNComponent({ company, companyWithTax, onClick }: TaxCardSNProps) {
  const {
    total_faturamento: totalFaturamento,
    vl_simples_nacional: vlSimplesNacional,
    simples_nacional_percentual: simplesNacionalPercentual,
    vl_saida: vlSaida,
    vl_servico: vlServico,
    vl_outros: vlOutros,
    iss,
    iss_retido: issRetido,
    apuracoes,
  } = companyWithTax;

  // Verifica se há múltiplas atividades/anexos
  const temMultiplasAtividades = apuracoes && apuracoes.length > 1;

  const getProgressColor = () => {
    if (simplesNacionalPercentual > 15) return 'red';
    if (simplesNacionalPercentual > 10) return 'orange';
    return 'green';
  };

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px"
      borderColor="gray.200"
      p={2.5}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: 'blue.300',
        boxShadow: 'sm',
        transform: 'translateY(-1px)',
      }}
      onClick={() => onClick(company)}
      minW={0}
      w="100%"
    >
      {/* Header compacto com ID */}
      <Flex justify="flex-start" align="center" mb={1.5}>
        <Text fontSize="2xs" fontWeight="600" color="gray.500" noOfLines={1} minW={0}>
          ID: {company.external_id}
        </Text>
      </Flex>

      {/* Nome da Empresa compacto */}
      <Flex align="center" gap={1.5} mb={1} minW={0}>
        <Icon as={Buildings} color="blue.500" boxSize={3.5} flexShrink={0} />
        <Text fontSize="xs" fontWeight="600" color="gray.800" noOfLines={1} minW={0}>
          {company.razao_social}
        </Text>
      </Flex>

      {/* CNPJ compacto */}
      <Text fontSize="2xs" color="gray.400" mb={2} fontFamily="mono" noOfLines={1}>
        {formatCNPJ(company.cnpj)}
      </Text>

      {/* Faturamento e Simples Nacional na mesma linha - compacto */}
      <Flex gap={1} mb={2}>
        {/* Faturamento Total */}
        <Box bg="blue.50" px={1.5} py={1} borderRadius="md" flex={1} minW={0}>
          <Flex align="center" gap={1}>
            <Icon as={CurrencyDollarSimple} color="blue.500" boxSize={3} flexShrink={0} />
            <VStack align="flex-start" spacing={0} flex={1} minW={0}>
              <Text fontSize="2xs" color="blue.600" fontWeight="500" noOfLines={1}>
                Faturamento
              </Text>
              <Text fontSize="2xs" fontWeight="700" color="blue.700" noOfLines={1}>
                {formatarEmReais(totalFaturamento)}
              </Text>
            </VStack>
          </Flex>
        </Box>

        {/* Simples Nacional */}
        <Box bg="green.50" px={1.5} py={1} borderRadius="md" flex={1} minW={0}>
          <Flex direction="column" gap={0.5}>
            <Flex align="center" gap={1}>
              <Icon as={Receipt} color="green.600" boxSize={3} flexShrink={0} />
              <Text fontSize="2xs" color="green.600" fontWeight="500" noOfLines={1} flex={1}>
                Simples
              </Text>
              <Badge
                colorScheme={getProgressColor()}
                fontSize="2xs"
                px={1}
                borderRadius="full"
                flexShrink={0}
              >
                {formatarPercentual(simplesNacionalPercentual)}
              </Badge>
            </Flex>
            <Text fontSize="2xs" fontWeight="700" color="green.700" noOfLines={1} pl={4}>
              {formatarEmReais(vlSimplesNacional)}
            </Text>
          </Flex>
        </Box>
      </Flex>

      {/* 3 Colunas: Tipos de Receita | ISS | Anexo/Seção/Tabela */}
      <Box borderTop="1px" borderColor="gray.100" pt={2}>
        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={1}>
          {/* Coluna 1: Tipos de Receita */}
          <VStack align="stretch" spacing={0.5} minW={0}>
            <Text
              fontSize="2xs"
              fontWeight="600"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.3px"
              noOfLines={1}
            >
              TIPOS DE RECEITA
            </Text>
            <Flex align="center" bg="gray.50" px={1} py={0.5} borderRadius="sm" gap={0.5}>
              <Box w={1} h={1} borderRadius="full" bg="#82ca9d" flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" noOfLines={1} flex={1}>
                S.
              </Text>
              <Text fontSize="2xs" fontWeight="600" color="gray.700" noOfLines={1}>
                {formatarEmReais(vlSaida)}
              </Text>
            </Flex>
            <Flex align="center" bg="gray.50" px={1} py={0.5} borderRadius="sm" gap={0.5}>
              <Box w={1} h={1} borderRadius="full" bg="#8884d8" flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" noOfLines={1} flex={1}>
                Sv.
              </Text>
              <Text fontSize="2xs" fontWeight="600" color="gray.700" noOfLines={1}>
                {formatarEmReais(vlServico)}
              </Text>
            </Flex>
            <Flex align="center" bg="gray.50" px={1} py={0.5} borderRadius="sm" gap={0.5}>
              <Box w={1} h={1} borderRadius="full" bg="#ffc658" flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" noOfLines={1} flex={1}>
                O.
              </Text>
              <Text fontSize="2xs" fontWeight="600" color="gray.700" noOfLines={1}>
                {formatarEmReais(vlOutros)}
              </Text>
            </Flex>
          </VStack>

          {/* Coluna 2: ISS */}
          <VStack align="stretch" spacing={0.5} minW={0}>
            <Text
              fontSize="2xs"
              fontWeight="600"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.3px"
              noOfLines={1}
            >
              ISS
            </Text>
            <Flex align="center" bg="gray.50" px={1} py={0.5} borderRadius="sm" gap={0.5}>
              <Box w={1} h={1} borderRadius="full" bg="#FF9F0A" flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" noOfLines={1} flex={1}>
                I.
              </Text>
              <Text fontSize="2xs" fontWeight="600" color="gray.700" noOfLines={1}>
                {formatarEmReais(iss)}
              </Text>
            </Flex>
            <Flex align="center" bg="gray.50" px={1} py={0.5} borderRadius="sm" gap={0.5}>
              <Box w={1} h={1} borderRadius="full" bg="#BF5AF2" flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" noOfLines={1} flex={1}>
                I.R.
              </Text>
              <Text fontSize="2xs" fontWeight="600" color="gray.700" noOfLines={1}>
                {formatarEmReais(issRetido)}
              </Text>
            </Flex>
          </VStack>

          {/* Coluna 3: Anexos SN (Anexo/Seção/Tabela) */}
          <VStack align="stretch" spacing={0.5} minW={0}>
            <Flex align="center" gap={1}>
              <Text
                fontSize="2xs"
                fontWeight="600"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.3px"
                noOfLines={1}
              >
                ATIVIDADES SN
              </Text>
              {temMultiplasAtividades && (
                <Badge colorScheme="purple" fontSize="2xs" px={1} borderRadius="full">
                  {apuracoes.length}
                </Badge>
              )}
            </Flex>
            {apuracoes && apuracoes.length > 0 ? (
              apuracoes.map((atividade, index) => (
                <Flex
                  key={`atividade-${atividade.anexo}-${atividade.secao ?? 0}-${atividade.tabela ?? 0}-${atividade.valor_guia}`}
                  align="center"
                  bg={index % 2 === 0 ? 'gray.50' : 'purple.50'}
                  px={1}
                  py={0.5}
                  borderRadius="sm"
                  gap={0.5}
                  borderLeft="2px solid"
                  borderColor={index % 2 === 0 ? '#ff8042' : '#BF5AF2'}
                >
                  <Text fontSize="2xs" fontWeight="600" color="gray.700" noOfLines={1}>
                    A{atividade.anexo}
                    {atividade.secao ? `/S${atividade.secao}` : ''}
                    {atividade.tabela ? `/T${atividade.tabela}` : ''}
                  </Text>
                  <Text fontSize="2xs" color="gray.500" ml="auto" noOfLines={1}>
                    {formatarEmReais(atividade.valor_guia)}
                  </Text>
                </Flex>
              ))
            ) : (
              <Text fontSize="2xs" color="gray.400" fontStyle="italic">
                Sem atividades
              </Text>
            )}
          </VStack>
        </SimpleGrid>
      </Box>
    </Box>
  );
}

export const TaxCardSN = React.memo(TaxCardSNComponent, (prevProps, nextProps) => {
  // Comparação customizada para evitar re-renders desnecessários
  return (
    prevProps.company.id === nextProps.company.id &&
    prevProps.company.external_id === nextProps.company.external_id &&
    prevProps.companyWithTax.external_id === nextProps.companyWithTax.external_id &&
    prevProps.companyWithTax.total_faturamento === nextProps.companyWithTax.total_faturamento &&
    prevProps.companyWithTax.vl_simples_nacional === nextProps.companyWithTax.vl_simples_nacional &&
    prevProps.companyWithTax.simples_nacional_percentual ===
      nextProps.companyWithTax.simples_nacional_percentual
  );
});
